// Product Custom Variant Switcher
// Handles image switching when variant changes (Color + Type)
// Images switch by variant ID - each color+type combo has its own main and bottom image

if (!customElements.get('product-custom-variant-switcher')) {
  customElements.define('product-custom-variant-switcher', class ProductCustomVariantSwitcher extends HTMLElement {
    constructor() {
      super();
      this.bigImageContainer = null;
      this.bottomImageContainer = null;
      this.currentVariantId = null; // Track current variant by ID
      this.productVariants = []; // Store product variants data
      this.debug = false; // Enable debug logging (set to true for debugging)
    }

    log(...args) {
      if (this.debug) {
        console.log('[Variant Switcher]', ...args);
      }
    }

    connectedCallback() {
      // Get containers after element is connected to DOM
      this.bigImageContainer = this.querySelector('.product-custom__big-image');
      this.bottomImageContainer = this.querySelector('.product-custom__bottom-image');

      if (!this.bigImageContainer || !this.bottomImageContainer) {
        console.warn('Product Custom Variant Switcher: Missing image containers');
        return;
      }

      this.log('Initialized');
      this.log('Big image container:', this.bigImageContainer);
      this.log('Bottom image container:', this.bottomImageContainer);

      // Get product variants data from Shopify's JSON
      this.loadProductVariants();

      // Set initial state
      this.initializeDefaultVariant();

      // Setup URL preservation for view parameter
      this.setupUrlPreservation();

      // Setup event listeners
      this.setupEventListeners();

      // Log initialization summary
      this.logInitializationSummary();

      // Expose global helper for debugging
      window.debugVariantSwitcher = () => this.debugInfo();

      // SAFETY NET: Re-sync after a short delay to handle race conditions
      // This ensures images match selection even if variant picker loads slowly
      setTimeout(() => {
        this.syncWithCurrentSelection();
      }, 200);
    }

    loadProductVariants() {
      // Try to get variants from Shopify's product JSON script
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          const product = JSON.parse(productJson.textContent);
          this.productVariants = product.variants || [];
          this.log('Loaded product variants:', this.productVariants.length);
        } catch (e) {
          this.log('Could not parse product JSON:', e);
        }
      }

      // Also build from images if JSON not available
      if (this.productVariants.length === 0) {
        const bigImages = this.bigImageContainer.querySelectorAll('img[data-variant-id]');
        bigImages.forEach(img => {
          const variantId = img.dataset.variantId;
          if (variantId) {
            this.productVariants.push({ id: parseInt(variantId) });
          }
        });
        this.log('Built variants from images:', this.productVariants.length);
      }
    }
    
    syncWithCurrentSelection() {
      this.log('🔄 Running delayed sync check...');

      // Get variant ID from URL or find matching variant from selected options
      const variantId = this.getCurrentVariantId();

      if (variantId && variantId !== this.currentVariantId) {
        this.log(`⚠️ Mismatch detected! Current: ${this.currentVariantId}, Should be: ${variantId}`);
        this.log('🔧 Fixing mismatch...');
        this.currentVariantId = variantId;
        this.switchImagesByVariantId(variantId);
      } else if (variantId === this.currentVariantId) {
        this.log('✅ Sync check passed - images match selection');
      }
    }

    getCurrentVariantId() {
      // PRIORITY 1: Get from URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      if (urlVariantId) {
        this.log('Got variant ID from URL:', urlVariantId);
        return urlVariantId;
      }

      // PRIORITY 2: Find matching variant from selected options
      return this.findVariantIdFromSelectedOptions();
    }

    findVariantIdFromSelectedOptions() {
      // Get all selected options
      const selectedOptions = [];
      const variantSelects = document.querySelector('variant-selects');

      if (variantSelects) {
        // Get option values from radios or selects
        const colorInput = variantSelects.querySelector('input[name*="Color"]:checked, select[name*="Color"]');
        const typeInput = variantSelects.querySelector('input[name*="Type"]:checked, select[name*="Type"]');

        if (colorInput) {
          selectedOptions.push(colorInput.value);
        }
        if (typeInput) {
          selectedOptions.push(typeInput.value);
        }

        this.log('Selected options:', selectedOptions);

        // Find matching variant
        if (this.productVariants.length > 0 && selectedOptions.length > 0) {
          const matchingVariant = this.productVariants.find(variant => {
            // Check if variant options match selected options
            const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
            return selectedOptions.every(selected =>
              variantOptions.some(opt => opt && opt.toLowerCase().includes(selected.toLowerCase()) ||
                                         selected.toLowerCase().includes(opt && opt.toLowerCase()))
            );
          });

          if (matchingVariant) {
            this.log('Found matching variant:', matchingVariant.id);
            return matchingVariant.id.toString();
          }
        }
      }

      // Fallback: Get from visible image
      const visibleImage = this.bigImageContainer.querySelector('img[data-variant-id][style*="display: block"]');
      if (visibleImage) {
        return visibleImage.dataset.variantId;
      }

      return null;
    }

    logInitializationSummary() {
      console.group('🎨 Product Variant Switcher - Initialization Summary');
      console.log('✅ Component initialized successfully');
      console.log('📍 Current variant ID:', this.currentVariantId);
      console.log('🖼️ Big images found:', this.bigImageContainer.querySelectorAll('img[data-variant-id]').length);
      console.log('🖼️ Bottom images found:', this.bottomImageContainer.querySelectorAll('img[data-variant-id]').length);
      console.log('🎛️ Product variants loaded:', this.productVariants.length);
      console.log('💡 Debug mode:', this.debug ? 'ON' : 'OFF');
      console.log('💡 Type "debugVariantSwitcher()" in console for detailed info');
      console.groupEnd();
    }

    debugInfo() {
      console.group('🔍 Variant Switcher Debug Info');
      console.log('Current variant ID:', this.currentVariantId);
      console.log('Product variants:', this.productVariants.length);

      console.group('Big Images:');
      const bigImages = this.bigImageContainer.querySelectorAll('img[data-variant-id]');
      bigImages.forEach(img => {
        console.log(`- ID: ${img.dataset.variantId} | ${img.alt} (visible: ${img.style.display !== 'none'})`);
      });
      console.groupEnd();

      console.group('Bottom Images:');
      const bottomImages = this.bottomImageContainer.querySelectorAll('img[data-variant-id]');
      bottomImages.forEach(img => {
        console.log(`- ID: ${img.dataset.variantId} | ${img.alt} (visible: ${img.style.display !== 'none'})`);
      });
      console.groupEnd();

      console.group('Variant Inputs:');
      const inputs = document.querySelectorAll('input[name*="options"], select[name*="options"]');
      inputs.forEach(input => {
        console.log(`- ${input.name}: ${input.value} (checked: ${input.checked || input.selected})`);
      });
      console.groupEnd();

      console.groupEnd();
      return 'Debug info logged above ☝️';
    }

    setupUrlPreservation() {
      // Check if we're in custom view mode
      const urlParams = new URLSearchParams(window.location.search);
      this.hasViewParam = urlParams.has('view');
      this.viewParamValue = urlParams.get('view');
      
      this.log('URL has view parameter:', this.hasViewParam, 'Value:', this.viewParamValue);

      // Listen to URL changes (back/forward buttons, variant changes)
      window.addEventListener('popstate', () => {
        this.log('popstate event - URL changed');
        this.handleUrlChange();
      });

      // Intercept history.pushState to preserve view parameter
      const originalPushState = history.pushState;
      const self = this;
      history.pushState = function(...args) {
        if (self.hasViewParam && args[2]) {
          const url = new URL(args[2], window.location.origin);
          if (!url.searchParams.has('view')) {
            url.searchParams.set('view', self.viewParamValue);
            args[2] = url.pathname + url.search;
            self.log('Preserved view parameter in pushState:', args[2]);
          }
        }
        const result = originalPushState.apply(history, args);
        // Trigger our handler after URL change
        setTimeout(() => self.handleUrlChange(), 50);
        return result;
      };

      // Intercept history.replaceState to preserve view parameter
      const originalReplaceState = history.replaceState;
      history.replaceState = function(...args) {
        if (self.hasViewParam && args[2]) {
          const url = new URL(args[2], window.location.origin);
          if (!url.searchParams.has('view')) {
            url.searchParams.set('view', self.viewParamValue);
            args[2] = url.pathname + url.search;
            self.log('Preserved view parameter in replaceState:', args[2]);
          }
        }
        const result = originalReplaceState.apply(history, args);
        // Trigger our handler after URL change
        setTimeout(() => self.handleUrlChange(), 50);
        return result;
      };
    }

    handleUrlChange() {
      // Check current URL for variant parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      this.log('URL changed. Variant ID:', variantId);

      if (variantId && variantId !== this.currentVariantId) {
        this.log('Variant ID changed in URL, switching images...');
        this.currentVariantId = variantId;
        this.switchImagesByVariantId(variantId);
      }
    }

    setupEventListeners() {
      // PRIMARY METHOD: Listen to variant-selects custom element change event
      // This is the MAIN way Shopify handles variant changes
      const variantSelects = document.querySelector('variant-selects');
      
      if (variantSelects) {
        this.log('✅ Found variant-selects element - Setting up primary listener');
        
        // Listen to change event on the variant-selects container
        variantSelects.addEventListener('change', (event) => {
          this.log('🎯 variant-selects CHANGE event fired!', event.target);
          
          // Get the actual input/select that changed
          const changedInput = event.target;
          this.log('Changed input:', {
            tagName: changedInput.tagName,
            type: changedInput.type,
            name: changedInput.name,
            value: changedInput.value
          });
          
          // Handle the change
          this.handleVariantSelectChange(changedInput);
        });

        // ALSO listen for input events (for immediate feedback)
        variantSelects.addEventListener('input', (event) => {
          this.log('📝 variant-selects INPUT event fired!', event.target);
          this.handleVariantSelectChange(event.target);
        });

        // Find all variant option inputs for initial logging
        const allInputs = variantSelects.querySelectorAll('input[type="radio"], select');
        this.log(`Found ${allInputs.length} variant inputs inside variant-selects:`, allInputs);
        
        allInputs.forEach((input, index) => {
          this.log(`  Input ${index}:`, {
            type: input.tagName === 'SELECT' ? 'select' : input.type,
            name: input.name,
            value: input.value,
            id: input.id
          });
        });

      } else {
        console.warn('⚠️ variant-selects element NOT found! Variant switching may not work.');
        console.warn('Make sure the variant_picker block is added to the product page.');
      }

      // FALLBACK METHOD: Direct listeners on inputs (in case variant-selects doesn't fire)
      this.setupFallbackListeners();
    }

    setupFallbackListeners() {
      this.log('Setting up fallback listeners...');
      
      // Listen for any change on radio buttons or selects
      document.addEventListener('change', (event) => {
        const target = event.target;
        
        // Check if it's a variant option input
        if (target.matches('input[type="radio"][name*="-"]') || 
            target.matches('select[name^="options"]')) {
          this.log('🔄 Fallback listener caught change:', target);
          this.handleVariantSelectChange(target);
        }
      });

      // Also listen for clicks on labels (for radio buttons)
      document.addEventListener('click', (event) => {
        const label = event.target.closest('label[for]');
        if (label) {
          const forId = label.getAttribute('for');
          const input = document.getElementById(forId);
          
          if (input && input.type === 'radio') {
            this.log('🖱️ Label clicked for radio:', input);
            // Small delay to let the radio button update
            setTimeout(() => {
              this.handleVariantSelectChange(input);
            }, 10);
          }
        }
      });
    }

    handleVariantSelectChange(input) {
      this.log('🎯 Handling variant option change:', input.name, input.value);

      // Wait a tick for Shopify to update URL with new variant ID
      setTimeout(() => {
        const variantId = this.getCurrentVariantId();

        if (variantId) {
          if (variantId !== this.currentVariantId) {
            this.log(`🔀 Switching from variant ${this.currentVariantId} to ${variantId}`);
            this.currentVariantId = variantId;
            this.switchImagesByVariantId(variantId);
          } else {
            // Force refresh even if same variant (in case of race condition)
            this.log(`♻️ Same variant ${variantId} - forcing refresh`);
            this.switchImagesByVariantId(variantId);
          }
        } else {
          this.log('⚠️ Could not determine variant ID');
        }
      }, 50);
    }



    initializeDefaultVariant() {
      this.log('=== Initializing Default Variant ===');

      // Get initial variant ID
      const variantId = this.getCurrentVariantId();

      if (variantId) {
        this.log('✅ Setting initial variant ID:', variantId);
        this.currentVariantId = variantId;
        // Images are already set by Liquid based on selected_variant
        // Just make sure they're in sync
        this.switchImagesByVariantId(variantId);
      } else {
        // Fallback: Get from visible image
        const visibleBigImage = this.bigImageContainer.querySelector('img[data-variant-id][style*="display: block"]');
        if (visibleBigImage) {
          this.currentVariantId = visibleBigImage.dataset.variantId;
          this.log('Initial variant from visible image:', this.currentVariantId);
        }
      }

      this.log('✅ Initial variant ID set to:', this.currentVariantId);
      this.logAvailableImages();
    }
    
    logAvailableImages() {
      // Log all available images for debugging
      const allBigImages = this.bigImageContainer.querySelectorAll('img[data-variant-id]');
      const allBottomImages = this.bottomImageContainer.querySelectorAll('img[data-variant-id]');

      this.log('Available big images:', Array.from(allBigImages).map(img => `${img.dataset.variantId}: ${img.alt}`));
      this.log('Available bottom images:', Array.from(allBottomImages).map(img => `${img.dataset.variantId}: ${img.alt}`));
    }

    switchImagesByVariantId(variantId) {
      this.log('=== SWITCHING IMAGES TO VARIANT ID:', variantId, '===');

      // Add visual feedback during switch
      this.addSwitchingFeedback();

      // Switch Big Image by variant ID
      this.switchBigImageByVariantId(variantId);

      // Switch Bottom Image by variant ID
      this.switchBottomImageByVariantId(variantId);

      // Remove feedback after animation
      setTimeout(() => this.removeSwitchingFeedback(), 300);
    }

    addSwitchingFeedback() {
      // Add a subtle visual indicator that switching is happening
      if (this.bigImageContainer) {
        this.bigImageContainer.style.transition = 'opacity 0.2s ease';
        this.bigImageContainer.style.opacity = '0.7';
      }
      if (this.bottomImageContainer) {
        this.bottomImageContainer.style.transition = 'opacity 0.2s ease';
        this.bottomImageContainer.style.opacity = '0.7';
      }
    }

    removeSwitchingFeedback() {
      if (this.bigImageContainer) {
        this.bigImageContainer.style.opacity = '1';
      }
      if (this.bottomImageContainer) {
        this.bottomImageContainer.style.opacity = '1';
      }
    }

    switchBigImageByVariantId(variantId) {
      const allImages = this.bigImageContainer.querySelectorAll('img[data-variant-id]');

      this.log(`Switching big image to variant ID: ${variantId}`);
      this.log(`Found ${allImages.length} variant images in big image container`);

      let foundMatch = false;
      allImages.forEach(img => {
        const imgVariantId = img.dataset.variantId;
        this.log(`Checking image: ${img.alt} (data-variant-id: ${imgVariantId})`);

        if (imgVariantId === variantId) {
          img.style.display = 'block';
          // Add temporary highlight for debugging
          this.highlightImage(img);
          this.log('✓ SHOWING:', img.alt);
          foundMatch = true;
        } else {
          img.style.display = 'none';
          this.log('✗ Hiding:', img.alt);
        }
      });

      if (!foundMatch) {
        console.warn('⚠️ WARNING: No matching big image found for variant ID:', variantId);
        console.warn('Available images:', Array.from(allImages).map(img => `ID: ${img.dataset.variantId} | ${img.alt}`));
        this.showImageNotFoundWarning('big', variantId);
      } else {
        this.log('✅ Successfully switched big image to variant ID:', variantId);
      }
    }

    switchBottomImageByVariantId(variantId) {
      const allImages = this.bottomImageContainer.querySelectorAll('img[data-variant-id]');

      this.log(`Switching bottom image to variant ID: ${variantId}`);
      this.log(`Found ${allImages.length} variant images in bottom image container`);

      let foundMatch = false;
      allImages.forEach(img => {
        const imgVariantId = img.dataset.variantId;
        this.log(`Checking image: ${img.alt} (data-variant-id: ${imgVariantId})`);

        if (imgVariantId === variantId) {
          img.style.display = 'block';
          // Add temporary highlight for debugging
          this.highlightImage(img);
          this.log('✓ SHOWING:', img.alt);
          foundMatch = true;
        } else {
          img.style.display = 'none';
          this.log('✗ Hiding:', img.alt);
        }
      });

      if (!foundMatch) {
        console.warn('⚠️ WARNING: No matching bottom image found for variant ID:', variantId);
        console.warn('Available images:', Array.from(allImages).map(img => `ID: ${img.dataset.variantId} | ${img.alt}`));
        this.showImageNotFoundWarning('bottom', variantId);
      } else {
        this.log('✅ Successfully switched bottom image to variant ID:', variantId);
      }
    }

    highlightImage(img) {
      // Add temporary border highlight for visual debugging
      if (this.debug) {
        const originalBorder = img.style.border;
        const originalBorderRadius = img.style.borderRadius;
        
        // Apply debug border
        img.style.border = '3px solid #93FFFB';
        img.style.borderRadius = '10px';
        
        // Restore after 1 second
        setTimeout(() => {
          if (originalBorder) {
            img.style.border = originalBorder;
          } else {
            // Remove inline style to let CSS take over
            img.style.removeProperty('border');
          }
          
          if (originalBorderRadius) {
            img.style.borderRadius = originalBorderRadius;
          } else {
            img.style.removeProperty('border-radius');
          }
        }, 1000);
      }
    }

    showImageNotFoundWarning(imageType, variantId) {
      // Show a console warning with helpful debugging info
      console.group(`⚠️ Image Not Found: ${imageType} image`);
      console.warn(`Looking for variant ID: ${variantId}`);
      console.warn('Make sure your product images are set up correctly:');
      console.warn('- Main images: Link via Variant Image column in Matrixify import');
      console.warn('- Bottom images: Add alt text format "AERO PinkDust bottom - single"');
      console.warn('  where color and type match the variant');
      console.groupEnd();
    }
  });
}
